import React, { useMemo } from 'react';
import Avatar from '@mui/material/Avatar';
import makeStyles from '@mui/styles/makeStyles';
import StickyNote2SharpIcon from '@mui/icons-material/StickyNote2Sharp';
import classNames from 'classnames';
import { TimeSignalSource } from 'graphql/types';
import { useTIWIconStyles } from 'modules/notes/list/NoteSourceGroup';
import config from 'config';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    maxWidth: theme.spacing(8),
    flexFlow: 'wrap',
    alignContent: 'flex-start',
    paddingLeft: theme.spacing(0)
  }
}));

export const useAvatarStyles = makeStyles((theme) => ({
  root: {
    borderWidth: `${theme.spacing(0)} !important`,
    margin: theme.spacing(0.5),
    fontSize: theme.typography.fontSize,
    color: theme.palette.text.secondary
  }
}));

type NoteSourcesProps = {
  sources: TimeSignalSource[];
  className?: string;
  maxAvatarCount?: number;
};

export const DEFAULT_MAX_AVATAR_ITEMS = 6;

export const NoteSourcesTable: React.FC<NoteSourcesProps> = ({
  sources,
  className,
  maxAvatarCount = DEFAULT_MAX_AVATAR_ITEMS
}: NoteSourcesProps) => {
  const classes = useStyles();
  const manualNoteAvatarclasses = useTIWIconStyles({ isItemGrouped: false });
  const avatarClasses = useAvatarStyles();
  const avatarSize = useMemo(() => ({ width: 24, height: 24 }), []);
  const isSourcesOverflow = sources.length > maxAvatarCount;
  const overflowCount = isSourcesOverflow
    ? sources.length - maxAvatarCount + 1
    : 0;

  const { filteredSources, overflowSources } = useMemo(() => {
    const overflowSources = isSourcesOverflow
      ? sources.slice(-overflowCount)
      : [];
    return {
      filteredSources: isSourcesOverflow
        ? sources.slice(0, maxAvatarCount - 1)
        : sources,
      overflowSources
    };
  }, [sources, isSourcesOverflow, overflowCount, maxAvatarCount]);

  if (sources.length === 0) {
    return null;
  }

  return (
    <div
      className={classNames(classes.root, className)}
      data-testid={'note-sources-table'}
    >
      {filteredSources.map((source, index) => {
        if (source.id === config.clientId) {
          return (
            <Avatar
              data-testid={`note-sources-${index}-${source.name}`}
              key={source.id}
              alt={source.name}
              className={manualNoteAvatarclasses.tiwAvatar}
              classes={avatarClasses}
              sx={avatarSize}
            >
              <StickyNote2SharpIcon
                className={manualNoteAvatarclasses.tiwIcon}
              />
            </Avatar>
          );
        }
        if (source.image) {
          return (
            <Avatar
              data-testid={`note-sources-${index}-${source.image?.src}`}
              key={source.id}
              alt={source.name}
              src={source.image?.src}
              variant="square"
              classes={avatarClasses}
              sx={avatarSize}
            />
          );
        }
      })}

      {isSourcesOverflow && overflowSources.length && (
        <Avatar
          variant="circular"
          classes={avatarClasses}
          sx={avatarSize}
          data-testid={'note-sources-overflow'}
        >
          +{overflowCount}
        </Avatar>
      )}
    </div>
  );
};
